import type { TranslationCatalog } from './types';

export const es: TranslationCatalog = {
  localeName: 'Español',
  dir: 'ltr',
  brand: { wordmark: 'please.repair', product: 'Please.repair' },
  nav: {
    signIn: 'Iniciar sesión',
    tenants: 'Para inquilinos',
    advocates: 'Para defensores',
    landlords: 'Para propietarios y agentes',
    joinWaitlist: 'Unirse a la lista',
    dashboard: 'Panel', // drafted — not in design corpus
    knowledge: 'Conocimiento', // drafted — not in design corpus
  },
  hero: {
    title: 'Reparaciones de alquiler, resueltas como corresponde.',
    subtitle:
      'Please.repair sigue cada solicitud de reparación desde el primer aviso hasta el trabajo terminado. Si se estanca, te guía por el siguiente paso legal en tu estado o territorio.',
    cta: 'Unirse a la lista',
  },
  audiences: {
    tenant: {
      title: 'Soy inquilino',
      blurb: 'Reporta un problema, guarda tus pruebas y sabe qué hacer si nada ocurre.',
    },
    advocate: {
      title: 'Estoy ayudando a un inquilino',
      blurb:
        'Una amistad, un familiar, un trabajador social o un centro legal. Lleva el caso a su lado, con calma y según las reglas.',
    },
    landlord: {
      title: 'Soy propietario o administrador',
      blurb:
        'Una sola bandeja para cada solicitud, con el historial documentado que te mantiene en regla y fuera de audiencias.',
    },
  },
  waitlist: {
    heading: 'Consigue Please.repair primero',
    emailLabel: 'Tu correo',
    submit: 'Unirse a la lista',
    success: 'Ya estás en la lista. Hablamos pronto.',
    finePrint: 'Sin spam, aproximadamente un correo al mes.',
  },
  signIn: {
    heading: 'Inicia sesión o crea tu cuenta',
    stages: ['Correo', 'Código', 'Legal', 'Roles', 'Propiedades'],
    comingNote: 'La aplicación completa llegará pronto. Lo que ves aquí es una vista previa.', // drafted — not in design corpus
  },
  roles: {
    tenant: 'Inquilino',
    advocate: 'Defensor',
    propertyManager: 'Administrador',
    owner: 'Propietario',
    tradie: 'Profesional',
  },
  statuses: {
    open: 'Abierto',
    acknowledged: 'Acusado',
    scheduled: 'Agendado',
    awaitingQuote: 'Esperando presupuesto',
    quoted: 'Cotizado',
    overdue: 'Vencido',
    escalated: 'Escalado',
    resolved: 'Resuelto',
  },
  severities: { urgent: 'Urgente', high: 'Alta', routine: 'Rutinaria' },
  dashboard: {
    title: 'Panel completo',
    kpiOpen: 'Casos abiertos',
    kpiDueWeek: 'Vencen esta semana',
    kpiOverdue: 'Vencidos',
    kpiResolved30: 'Resueltos · 30 días',
    filterAll: 'Todos',
    filterOpen: 'Abiertos',
    filterDueSoon: 'Por vencer',
    filterOverdue: 'Vencidos',
    searchPlaceholder: 'Buscar casos, propiedades, personas',
    emptyQueue: 'Aún no hay nada',
    timelineHeading: 'Línea de tiempo',
    deadlineHeading: 'Plazo legal',
    sampleDataBadge: 'Datos de ejemplo', // drafted — not in design corpus
    chartTitle: 'Casos abiertos por gravedad', // drafted — not in design corpus
    viewAsTable: 'Ver como tabla', // drafted — not in design corpus
    viewAsChart: 'Ver como gráfico', // drafted — not in design corpus
  },
  knowledge: {
    heading: 'Conocimiento', // drafted — not in design corpus
    intro:
      'Las normas de reparación en viviendas de alquiler de cada estado y territorio: listas de reparaciones urgentes, plazos de reparación, tribunales y dónde obtener ayuda.', // ADR 0018 — neutral, matching clocksHeading
    disclaimer:
      'Please.repair organiza los hechos y señala las normas y formularios oficiales. No ofrece asesoramiento jurídico.',
    lastVerified: 'Última verificación', // drafted — not in design corpus
    urgentHeading: 'Reparaciones urgentes', // drafted — not in design corpus
    clocksHeading: 'Plazos de reparación',
    bodiesHeading: 'Tribunales y organismos', // drafted — not in design corpus
    helpHeading: 'Dónde obtener ayuda', // drafted — not in design corpus
    citationsHeading: 'Referencias',
    verifyFlagsHeading: 'Marcas de verificación', // drafted — not in design corpus
    clockStatutory: 'Legal · {period}',
    clockBenchmark: 'Referencia práctica · unos {period}',
  },
  a11y: {
    skipToContent: 'Saltar al contenido',
    opensInNewTab: 'se abre en una pestaña nueva',
    loading: 'Cargando…', // drafted — not in design corpus
  },
  errors: {
    generic: 'Algo salió mal.', // drafted — not in design corpus
    retry: 'Reintentar', // drafted — not in design corpus
    notFound: 'Página no encontrada', // drafted — not in design corpus
    notFoundBody: 'Esa página no existe o se ha movido.', // drafted — not in design corpus
    serverError: 'Algo salió mal', // drafted — #376
    serverErrorBody: 'No se pudo mostrar esta página. Reintenta o vuelve a la página de inicio.', // drafted — #376
    offline: 'Sin conexión', // drafted — #376
    offlineBody:
      'Please.repair necesita conexión para cargar esta página. Vuelve a conectarte e inténtalo de nuevo.', // drafted — #376
  },
  meta: {
    home: {
      title: 'Please.repair — reparaciones de alquiler con registro',
      description:
        'Please.repair — reparaciones de alquiler con registro. Reporta problemas, conserva pruebas, sigue los plazos de reparación y coordina a todos los implicados.',
    },
    tenants: {
      title: 'Para inquilinos — Please.repair',
      description: 'Reporta un problema, guarda tus pruebas y sabe qué hacer si nada ocurre.',
    },
    advocates: {
      title: 'Para defensores — Please.repair',
      description:
        'Una amistad, un familiar, un trabajador social o un centro legal. Lleva el caso a su lado, con calma y según las reglas.',
    },
    landlords: {
      title: 'Para propietarios y agentes — Please.repair',
      description:
        'Una sola bandeja para cada solicitud, con el historial documentado que te mantiene en regla y fuera de audiencias.',
    },
    signIn: {
      title: 'Iniciar sesión — Please.repair',
      description: 'Inicia sesión o crea tu cuenta de Please.repair.',
    },
    dashboard: {
      title: 'Panel — Please.repair',
      description: 'Casos abiertos, plazos y actividad reciente en una sola bandeja.',
    },
    knowledge: {
      title: 'Normas de reparación por estado y territorio — Please.repair',
      description:
        'Las normas de reparación en viviendas de alquiler de cada estado y territorio: listas de reparaciones urgentes, plazos de reparación, tribunales y dónde obtener ayuda.',
    },
    knowledgeDetail: {
      title: 'Normas de reparación — Please.repair',
      description:
        'Listas de reparaciones urgentes, plazos de reparación, tribunales y dónde obtener ayuda, jurisdicción por jurisdicción.',
    },
    offline: {
      title: 'Sin conexión — Please.repair',
      description:
        'Please.repair necesita conexión para cargar esta página. Vuelve a conectarte e inténtalo de nuevo.',
    },
    serverError: {
      title: 'Algo salió mal — Please.repair',
      description: 'No se pudo mostrar esta página. Reintenta o vuelve a la página de inicio.',
    },
    notFound: {
      title: 'Página no encontrada — Please.repair',
      description: 'Esa página no existe o se ha movido.',
    },
  },
  footer: {
    disclaimer:
      'Please.repair organiza los hechos y señala las normas y formularios oficiales. No ofrece asesoramiento jurídico.',
    rights: '© 2026 Aperim Pty Ltd · ABN 46 150 699 737',
  },
};
